import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Button,
  TableCell,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@parrot-software/design-system';
import {
  ChevronDown,
  ChevronRight,
  Download,
  Hourglass,
  RefreshCw,
  TriangleAlert,
} from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';
import type { PaymentHistoryViewDto } from '@subscriptions/application/dtos';
import { PaymentStatusBadge } from '@subscriptions/presentation/components/base/PaymentStatusBadge/PaymentStatusBadge';
import {
  displayPaymentValue,
  getPaymentMethodLabel,
  handleDownload,
  resolvePaymentInvoiceActions,
} from '@subscriptions/presentation/utils';

interface ExpandablePaymentRowProps {
  blockUuid?: string;
  payment: PaymentHistoryViewDto;
  isChild?: boolean;
  isExpanded?: boolean;
  isLastChild?: boolean;
  isRetryDisabled?: boolean;
  onToggle?: () => void;
  showConcept?: boolean;
  showSubscription?: boolean;
}

/**
 * ExpandablePaymentRow - Renders a single payment row for PaymentHistoryTable.
 *
 * Child rows stay mounted, animate their height on toggle, hide the values
 * repeated from the parent and drop their inner dividers.
 *
 * `showSubscription` and `showConcept` drop their cells.
 *
 * The last cell reports the state of this row's own CFDI, which the backend describes
 * independently of the charge — a settled payment can carry a failed invoice:
 *
 * | `invoiceStatus` | files | Cell |
 * |---|---|---|
 * | `ISSUED` | present | Download button |
 * | `ISSUED` | none | "Preparando archivos…" — the CFDI exists, its files are still being fetched |
 * | `FAILED` | none | Retry link; the reason travels in the link and is shown in the sheet |
 *
 * The cell belongs to whichever row holds the CFDI, which depends on the block's `group_invoice`
 * preference: an ungrouped payment holds its own; a group that invoices as one holds it on the
 * parent; a group that invoices per store holds one per child. A parent therefore yields the cell
 * as soon as any of its children reports an invoice of its own — but keeps a warning icon while
 * any of them offers a retry, so a failure is visible without expanding the row first.
 *
 * The retry link addresses `paymentUuid`, never `uuid` — child rows are keyed by the synthetic
 * `<paymentUuid>:<storeUuid>`, which the API would reject. Re-invoicing runs against the whole
 * payment even when the link sits on a single store's row.
 *
 * The retry link needs a block to address. The block detail view passes it as a prop; the
 * organization-level table has none loaded and falls back to `payment.blockUuid`, which the
 * payments API reports as `subscriptionUuid`.
 *
 * The fiscal record is read from the payment (`invoiceDataUuid`), never from the block's current
 * assignment: the retry re-invoices this charge against what it was invoiced with, so unassigning
 * the fiscal data afterwards must not withdraw the action. A payment with none was billed to
 * público general and has nothing to retry.
 *
 * The retry link is further limited to payments charged in the current month: re-invoicing an
 * already closed period is not accepted, so an older payment shows nothing instead of a link
 * that would fail. The window runs to the last day of that month — a charge collected on August
 * 11th keeps its link through August 31st and loses it on September 1st.
 *
 * That month is read from `createdAt`, never from `paymentDate`: the latter names the period
 * being billed and runs ahead of the charge, so reading it would date an August payment in
 * September and hide the link exactly while it is still usable.
 *
 * `isRetryDisabled` keeps the retry visible as a disabled button that leads nowhere.
 */
export const ExpandablePaymentRow = ({
  blockUuid,
  payment,
  isChild = false,
  isExpanded = false,
  isLastChild = false,
  isRetryDisabled = false,
  onToggle,
  showConcept = true,
  showSubscription = true,
}: ExpandablePaymentRowProps) => {
  const { t } = useTranslation('paymentHistory');
  const isExpandable = Boolean(onToggle);
  const isHiddenChild = isChild && !isExpanded;
  const { canDownload, isPreparingFiles, retryPath, hasRetryableChildren } = resolvePaymentInvoiceActions({
    blockUuid,
    isChild,
    payment,
  });
  const hideBottomDivider = isChild ? !(isLastChild && isExpanded) : isExpandable && isExpanded;

  const onDownload = () => {
    void handleDownload(payment.pdfUrl, payment.xmlUrl, payment.folio, {
      errorMessage: t('downloadError'),
      successMessage: t('downloadSuccess'),
      successDescription: t('downloadSuccessDescription'),
    });
  };

  /** Wraps child-row cell content so the row height animates between collapsed and expanded. */
  const renderCellContent = (content: ReactNode) => {
    if (!isChild) {
      return content;
    }

    return (
      <div
        className={cn('grid grid-rows-[0fr] transition-[grid-template-rows] duration-300 ease-in-out', {
          'grid-rows-[1fr]': isExpanded,
        })}
      >
        <div className="overflow-hidden">
          <div className="p-2.5">{content}</div>
        </div>
      </div>
    );
  };

  const cellClassName = cn({ 'p-0': isChild, 'p-2.5': !isChild });
  const numericCellClassName = cn('text-right', { 'p-0': isChild, 'p-2.5': !isChild });

  return (
    <TableRow
      aria-hidden={isHiddenChild || undefined}
      className={cn({ 'bg-muted/30': isChild, 'border-b-0': hideBottomDivider })}
      data-testid={isChild ? 'payment-history-child-row' : undefined}
    >
      <TableCell className={cn('whitespace-nowrap', { 'p-0': isChild, 'py-2.5 pl-1 pr-2.5': !isChild })}>
        {!isChild && (
          <div className="flex items-center gap-1">
            {isExpandable ? (
              <Button
                aria-expanded={isExpanded}
                aria-label={t(isExpanded ? 'expandable.collapse' : 'expandable.expand')}
                className="h-6 w-6 shrink-0 p-0"
                data-testid="payment-history-expand-toggle"
                onClick={onToggle}
                size="sm"
                variant="ghost"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-primary" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-primary" />
                )}
              </Button>
            ) : (
              <span aria-hidden className="h-6 w-6 shrink-0" />
            )}
            <span>{displayPaymentValue(payment.paymentDate)}</span>
          </div>
        )}
      </TableCell>
      {showSubscription && (
        <TableCell className={cellClassName}>{renderCellContent(displayPaymentValue(payment.subscription))}</TableCell>
      )}
      {showConcept && (
        <TableCell className={cellClassName}>{!isChild && displayPaymentValue(payment.concept)}</TableCell>
      )}
      <TableCell className={cellClassName}>{!isChild && <PaymentStatusBadge status={payment.status} />}</TableCell>
      <TableCell className={cn('hidden lg:table-cell', cellClassName)}>
        {!isChild && displayPaymentValue(getPaymentMethodLabel(payment.paymentMethod, payment.paymentMethodDetail, t))}
      </TableCell>
      <TableCell className={numericCellClassName}>
        {renderCellContent(displayPaymentValue(payment.subscriptionAmount))}
      </TableCell>
      <TableCell className={numericCellClassName}>
        {renderCellContent(
          <>
            {payment.isOverdueCharge && (
              <span className="text-sm text-warning" data-testid="payment-history-invoice-not-issued">
                {t('invoiceNotIssued')}
              </span>
            )}

            {canDownload && (
              <Button
                className="h-8 px-0"
                onClick={onDownload}
                size="sm"
                tabIndex={isHiddenChild ? -1 : undefined}
                variant="link"
              >
                <Download className="mr-1 h-3.5 w-3.5" />
                {t('downloadPdfXml')}
              </Button>
            )}

            {hasRetryableChildren && (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      aria-label={t('invoiceWarning')}
                      className="inline-flex h-8 w-8 items-center justify-center text-warning"
                      data-testid="payment-history-invoice-warning"
                      role="img"
                    >
                      <TriangleAlert className="h-4 w-4" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{t('invoiceWarning')}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {isPreparingFiles && (
              <span
                className="inline-flex h-8 items-center gap-1 whitespace-nowrap text-xs text-muted-foreground"
                data-testid="payment-history-preparing-files"
              >
                <Hourglass className="h-3.5 w-3.5" />
                {t('preparingFiles')}
              </span>
            )}

            {retryPath && isRetryDisabled && (
              <Button className="h-8 px-0 text-destructive" disabled size="sm" variant="link">
                <RefreshCw className="mr-1 h-3.5 w-3.5" />
                {t('retryInvoice')}
              </Button>
            )}

            {retryPath && !isRetryDisabled && (
              <Button asChild className="h-8 px-0 text-destructive" size="sm" variant="link">
                <Link data-testid="payment-history-retry-link" tabIndex={isHiddenChild ? -1 : undefined} to={retryPath}>
                  <RefreshCw className="mr-1 h-3.5 w-3.5" />
                  {t('retryInvoice')}
                </Link>
              </Button>
            )}
          </>,
        )}
      </TableCell>
    </TableRow>
  );
};
